import api from '../api';
import LibraryActions from 'actions/library/LibraryActions';

const libraryConfig = {baseURL: '/apiv2/library'};
const apiv2Config = {baseURL: '/apiv2'};

const interceptResponse = (response, action, args) => {
  if (
    action === LibraryActions.searchCallsSuccess ||
    action === LibraryActions.searchMostRecentlyAddedSuccess ||
    action === LibraryActions.getLibrarySuccess ||
    action === LibraryActions.addFileToLibrarySuccess ||
    action === LibraryActions.getTagsSuccess ||
    action === LibraryActions.deleteLibraryTagSuccess
  ) {
    return {args, response};
  } else {
    return response;
  }
};

/**
 * Interactions with /votes api
 */
const LibrarySource = {
  getFileInfo: {
    remote(state, file_upload_id) {
      return api.get(`/file/${file_upload_id}/`, libraryConfig);
    },
    loading: LibraryActions.getFileInfoLoading,
    success: LibraryActions.getFileInfoSuccess,
    error: LibraryActions.getFileInfoError,
  },

  getInfoAboutFiles: {
    remote(state, data) {
      return api.post('/files_info/', data, libraryConfig);
    },
    success: LibraryActions.getInfoAboutFilesSuccess,
    error: LibraryActions.getInfoAboutFilesError,
  },

  createLibrary: {
    remote(state, data) {
      return api.put('/', data, libraryConfig);
    },
    loading: LibraryActions.createLibraryLoading,
    success: LibraryActions.createLibrarySuccess,
    error: LibraryActions.createLibraryError,
  },

  updateLibrary: {
    remote(state, data) {
      return api.put('/', data, libraryConfig);
    },
    loading: LibraryActions.updateLibraryLoading,
    success: LibraryActions.updateLibrarySuccess,
    error: LibraryActions.updateLibraryError,
  },

  /**
   * Returns all libraries for a user.
   */
  getLibraries: {
    remote(state, adminManage) {
      if (adminManage) {
        return api.get('/?shared_only=true', libraryConfig);
      }

      return api.get('/', libraryConfig);
    },

    loading: LibraryActions.getLibrariesLoading,
    success: LibraryActions.getLibrariesSuccess,
    error: LibraryActions.getLibrariesError,
  },

  /**
   * Add a file to a library with a given list of tags
   */
  addFileToLibrary: {
    // changedHighlights is not used here but passed through to the onSuccess method
    remote(state, fileId, libraryId, tags, removeTags, changedHighlights) {
      return api.post(
        `/file/`,
        {
          file_upload_id: fileId,
          library_changes: [
            {
              library_id: libraryId,
              tags: tags,
              remove_tags: removeTags,
            },
          ],
        },
        libraryConfig
      );
    },

    interceptResponse: interceptResponse,
    loading: LibraryActions.addFileToLibraryLoading,
    success: LibraryActions.addFileToLibrarySuccess,
    error: LibraryActions.addFileToLibraryError,
  },

  /**
   * Returns all tags from all libraries the user has access to.
   */
  getTags: {
    remote(state, libraries, users) {
      let url = '/tags/';
      let librariesQueryParam = '';
      if (libraries && libraries.count() > 0) {
        libraries.forEach(libraryId => {
          librariesQueryParam += `libraries=${libraryId}&`;
        });
      }
      if (librariesQueryParam.length > 0) {
        url += `?${librariesQueryParam}`;
      }
      let usersQueryParam = '';
      if (users && users.count() > 0) {
        users.forEach(userId => {
          usersQueryParam += `users=${userId}&`;
        });
      }
      if (librariesQueryParam.length > 0) {
        if (usersQueryParam.length > 0) {
          url += `&${usersQueryParam}`;
        }
      } else if (usersQueryParam.length > 0) {
        url += `?${usersQueryParam}`;
      }
      return api.get(url, libraryConfig);
    },

    interceptResponse: interceptResponse,
    loading: LibraryActions.getTagsLoading,
    success: LibraryActions.getTagsSuccess,
    error: LibraryActions.getTagsError,
  },

  /**
   * Returns recent tags from all the libraries the user has access to.
   */
  getRecentTags: {
    remote(state) {
      return api.get('/tags/recent/', libraryConfig);
    },

    loading: LibraryActions.getRecentTagsLoading,
    success: LibraryActions.getRecentTagsSuccess,
    error: LibraryActions.getRecentTagsError,
  },

  /**
   * Returns common tags from all the libraries the user has access to.
   */
  getCommonTags: {
    remote(state) {
      return api.get('/tags/common/', libraryConfig);
    },

    loading: LibraryActions.getCommonTagsLoading,
    success: LibraryActions.getCommonTagsSuccess,
    error: LibraryActions.getCommonTagsError,
  },

  /**
   * Get the calls_count for a given library-tag pair
   */
  getLibraryTag: {
    remote(state, libraryId, tag) {
      return api.get(`/${libraryId}/tag/${encodeURIComponent(tag)}/`, libraryConfig);
    },
    loading: LibraryActions.getLibraryTagLoading,
    success: LibraryActions.getLibraryTagSuccess,
    error: LibraryActions.getLibraryTagError,
  },

  getShareItems: {
    remote(state) {
      return api.get('/share_items/', libraryConfig);
    },

    loading: LibraryActions.getShareItemsLoading,
    success: LibraryActions.getShareItemsSuccess,
    error: LibraryActions.getShareItemsError,
  },

  getShareAccessibleTeams: {
    remote(state) {
      return api.get('/teams_within_user_perms', apiv2Config);
    },

    loading: LibraryActions.getShareAccessibleTeamsLoading,
    success: LibraryActions.getShareAccessibleTeamsSuccess,
    error: LibraryActions.getShareAccessibleTeamsError,
  },

  getShareAccessibleDepartments: {
    remote(state) {
      return api.get('/departments_within_user_perms', apiv2Config);
    },

    loading: LibraryActions.getShareAccessibleDepartmentsLoading,
    success: LibraryActions.getShareAccessibleDepartmentsSuccess,
    error: LibraryActions.getShareAccessibleDepartmentsError,
  },

  getLibrary: {
    remote(state, libraryId, _) {
      return api.get('/' + libraryId + '/', libraryConfig);
    },

    interceptResponse: interceptResponse,
    loading: LibraryActions.getLibraryLoading,
    success: LibraryActions.getLibrarySuccess,
    error: LibraryActions.getLibraryError,
  },

  deleteLibrary: {
    remote(state, libraryId) {
      return api.delete('/' + libraryId + '/', libraryConfig);
    },

    loading: LibraryActions.deleteLibraryLoading,
    success: LibraryActions.deleteLibrarySuccess,
    error: LibraryActions.deleteLibraryError,
  },

  deleteLibraryTag: {
    remote(state, data) {
      return api.delete('/file/', {
        baseURL: libraryConfig.baseURL,
        data: data,
      });
    },

    interceptResponse: interceptResponse,
    loading: LibraryActions.deleteLibraryTagLoading,
    success: LibraryActions.deleteLibraryTagSuccess,
    error: LibraryActions.deleteLibraryTagError,
  },

  getCallersAutocomplete: {
    remote(state, input, library_id = null) {
      let url = `/callers/autocomplete/?q=${input}`;
      if (library_id) {
        url += `&library_id=${library_id}`;
      }
      return api.get(url, libraryConfig);
    },

    loading: LibraryActions.getCallersAutocompleteLoading,
    success: LibraryActions.getCallersAutocompleteSuccess,
    error: LibraryActions.getCallersAutocompleteError,
  },

  search: {
    remote(state, data) {
      return api.post('/search_calls/', data, libraryConfig);
    },

    interceptResponse: interceptResponse,
    loading: LibraryActions.searchCallsLoading,
    success: LibraryActions.searchCallsSuccess,
    error: LibraryActions.searchCallsError,
  },

  searchRecentlyAdded: {
    remote(state, page) {
      return api.post('/recently_added_calls/', {page: page}, libraryConfig);
    },

    interceptResponse: interceptResponse,
    loading: LibraryActions.searchMostRecentlyAddedLoading,
    success: LibraryActions.searchMostRecentlyAddedSuccess,
    error: LibraryActions.searchMostRecentlyAddedError,
  },
};

export default LibrarySource;
